import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Tooltip,
  CircularProgress,
  Alert,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  SelectChangeEvent,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  AttachFile as AttachFileIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ErrorOutline as ErrorOutlineIcon,
  Refresh as RefreshIcon,
  EmailOutlined as EmailOutlinedIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useTheme } from '../../contexts/ThemeContext';
import {
  type Skill,
  type SkillType,
  type Protocol,
  type HttpMethod,
  type AuthType,
  type AuthConfig,
  type Transport,
  type KnowledgeFile,
  type ValidateResult,
  type StdioCatalogKey,
  type StdioCommand,
  fetchSkills,
  createSkill,
  updateSkill,
  toggleSkillActive,
  deleteSkill as apiDeleteSkill,
  validateRestSkill,
  validateMcpSkill,
  validateSkillConnection,
  stripExtraHeaderIds,
  buildStdioArgs,
  discoverSchema,
  generateBusinessRules,
  uploadKnowledgeFiles,
  deleteDatasource,
  removeKnowledgeFile as apiRemoveKnowledgeFile,
  fetchKnowledgeFilesByFilenames,
  claimSkillDatasourceIds,
} from '../../services/skillsService';
import AddLogDataSourceModal from '../AddLogDataSourceModal';
import { STORAGE_KEYS } from '../../helpers/constants';
import {
  getTableCellStyles,
  getTableContainerStyles,
  getTableHeaderStyles,
  getTableRowStyles,
} from '../../styles/tableStyles';
import SkillAuthConfigForm from '../SkillAuthConfigForm';
import SkillAuthStatusBadge from '../SkillAuthStatusBadge';
import StdioMcpFields from '../StdioMcpFields';
import type { SkillAuthConfig } from '../../types/skill';

// ─── Default empty skill for "create" flow ────────────────────────────────────

const emptySkill = (): Skill => ({
  id: '',
  name: '',
  type: 'external',
  description: '',
  business_rules: '',
  is_active: true,
  is_preloaded: false,
  protocol: 'REST',
  auth_config: { type: 'none' },
  base_url: '',
  endpoint_path: '',
  method: 'POST',
  payload_schema: '',
  response_schema: '',
  mcp_server_url: '',
  transport: 'SSE',
  stdio_catalog: 'custom',
  stdio_command: 'uvx',
  stdio_package: '',
  stdio_version: '',
  stdio_env_rows: [],
  router_emails: '',
  threshold: 0,
  knowledge_files: [],
  tool_list: [],
});

/** Use with TextField `required` — do not add "*" to label (MUI adds one asterisk). */
const requiredTextFieldLabelProps = {
  InputLabelProps: {
    sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } },
  },
};

/**
 * Placeholder typography aligned across dialog fields.
 * Color/opacity omitted so MUI default muted placeholder grey is used (pre-change look).
 */
const skillDialogPlaceholderSx = {
  fontSize: '0.875rem',
  fontStyle: 'normal',
  fontWeight: 400,
  fontFamily: 'inherit',
};

const skillDialogTextFieldSx = {
  '& .MuiInputBase-input::placeholder': skillDialogPlaceholderSx,
};

/** JSON schema fields: monospace for typed values; placeholders match other dialog fields */
const skillDialogJsonTextFieldSx = {
  ...skillDialogTextFieldSx,
  '& .MuiInputBase-input': {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
  },
};

// AuthFields has been replaced by SkillAuthConfigForm (TASK-10)

// ─── Skill Form Dialog ────────────────────────────────────────────────────────

function getLoggedInUserEmail(): string {
  const tokenUserRaw = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA) || '{}';
  try {
    const parsedUser = JSON.parse(tokenUserRaw) as { email_id?: string; email?: string };
    return (parsedUser.email_id || parsedUser.email || '').trim();
  } catch {
    return '';
  }
}

interface SkillDialogProps {
  open: boolean;
  skill: Skill | null;
  channelId: string;
  workspaceTag?: string;
  onClose: () => void;
  onSaved: (skill: Skill) => void;
  disableInternalSkillType?: boolean;
  focusAuth?: boolean;
}

const SkillDialog: React.FC<SkillDialogProps> = ({
  open,
  skill,
  channelId,
  workspaceTag,
  onClose,
  onSaved,
  disableInternalSkillType = false,
  focusAuth = false,
}) => {
  const muiTheme = useMuiTheme();
  const isEdit = Boolean(skill?.id);

  const [form, setForm] = useState<Skill>(skill ?? emptySkill());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const [validateStatus, setValidateStatus] = useState<'idle' | 'loading' | 'success' | 'error' | 'warning'>('idle');
  const [validateMsg, setValidateMsg] = useState('');
  // stdio: tracks whether the explicit "Validate Connection" step has passed — gates Save.
  const [stdioValidatePassed, setStdioValidatePassed] = useState(false);
  // Router emails chip input
  const [routerEmailInput, setRouterEmailInput] = useState('');

  const [discoverLoading, setDiscoverLoading] = useState(false);
  const [discoverError, setDiscoverError] = useState('');

  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const [removingFileId, setRemovingFileId] = useState<string | null>(null);
  const [datasourceTab, setDatasourceTab] = useState<0 | 1>(0);
  const [addDsModalOpen, setAddDsModalOpen] = useState(false);
  const [linkingFiles, setLinkingFiles] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [businessRulesLoading, setBusinessRulesLoading] = useState(false);
  const [businessRulesError, setBusinessRulesError] = useState('');

  const businessRulesRequestIdRef = React.useRef(0);
  const businessRulesAbortRef = React.useRef<AbortController | null>(null);
  const authSectionRef = React.useRef<HTMLDivElement | null>(null);
  // Incremented on every open/close transition — lets in-flight handleDatasourceAdded polls detect staleness.
  const datasourceLinkGenerationRef = React.useRef(0);

  React.useEffect(() => {
    // Always cancel any in-flight datasource-link poll when open/skill changes (open OR close).
    datasourceLinkGenerationRef.current++;
    if (open) {
      setForm(skill ? { ...skill } : { ...emptySkill(), type: 'external' });
      setSaving(false);
      setSaveError('');
      setValidateStatus('idle');
      setValidateMsg('');
      setStdioValidatePassed(false);
      setRouterEmailInput('');
      setDiscoverError('');
      setPendingFiles([]);
      setUploading(false);
      setUploadError('');
      setRemovingFileId(null);
      setDatasourceTab(0);
      setAddDsModalOpen(false);
      setLinkingFiles(false);
      setLinkError('');
      setBusinessRulesLoading(false);
      setBusinessRulesError('');
      businessRulesAbortRef.current?.abort();
      businessRulesAbortRef.current = null;
      businessRulesRequestIdRef.current = 0;
    }
  }, [open, skill]);

  React.useEffect(() => {
    if (open && focusAuth) {
      const id = setTimeout(() => {
        authSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
      return () => clearTimeout(id);
    }
  }, [open, focusAuth]);

  const update = (partial: Partial<Skill>) => {
    // Any change to stdio config means the previous validation is stale.
    const stdioConfigKeys: (keyof Skill)[] = ['stdio_env_rows', 'stdio_package', 'stdio_version', 'stdio_command', 'stdio_catalog'];
    if (stdioConfigKeys.some((k) => k in partial)) {
      setStdioValidatePassed(false);
      setValidateStatus('idle');
      setValidateMsg('');
    }
    setForm((prev) => ({ ...prev, ...partial }));
  };

  useEffect(() => {
    const skillName = form.name.trim();
    const skillDescription = form.description.trim();
    const isExternalSkill = form.type === 'external';

    if (!open || isEdit || !channelId || !isExternalSkill || !skillName || !skillDescription) {
      businessRulesAbortRef.current?.abort();
      businessRulesAbortRef.current = null;
      setBusinessRulesLoading(false);
      return;
    }

    const emailId = getLoggedInUserEmail();
    const workspaceName = workspaceTag?.trim() ?? '';

    if (!emailId || !workspaceName) {
      businessRulesAbortRef.current?.abort();
      businessRulesAbortRef.current = null;
      setBusinessRulesLoading(false);
      setBusinessRulesError(
        !emailId
          ? 'Unable to generate business rules: user email is not available. Please sign in again.'
          : 'Unable to generate business rules: workspace is not set for this channel.'
      );
      return;
    }

    const timeout = window.setTimeout(async () => {
      businessRulesAbortRef.current?.abort();
      const abortController = new AbortController();
      businessRulesAbortRef.current = abortController;
      const requestId = ++businessRulesRequestIdRef.current;

      setBusinessRulesLoading(true);
      setBusinessRulesError('');

      try {
        const response = await generateBusinessRules(
          {
            email_id: emailId,
            workspace_name: workspaceName,
            categories: [workspaceName],
            is_business_rule: true,
            skill_name: skillName,
            skill_description: skillDescription,
            channel_id: channelId,
          },
          abortController.signal
        );

        if (abortController.signal.aborted || requestId !== businessRulesRequestIdRef.current) return;

        const rules = Array.isArray(response.business_rules) ? response.business_rules : [];
        if (rules.length > 0) {
          update({ business_rules: rules.join('\n') });
        }
      } catch (err) {
        if (abortController.signal.aborted) return;
        setBusinessRulesError(err instanceof Error ? err.message : 'Failed to generate business rules.');
      } finally {
        if (requestId === businessRulesRequestIdRef.current) {
          setBusinessRulesLoading(false);
        }
      }
    }, 600);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [open, isEdit, channelId, workspaceTag, form.name, form.description, form.type]);

  // ── Save ──────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!form.name.trim() || saving) return;
    setSaving(true);
    setSaveError('');
    try {
      // stdio: Save is gated on stdioValidatePassed (button disabled until validated).
      // Safety guard in case the gate is somehow bypassed.
      if (isStdio && !stdioValidatePassed) {
        setSaveError('Please validate the connection before saving.');
        return;
      }

      let saved: Skill;
      if (isEdit) {
        saved = await updateSkill(form, channelId);
      } else {
        saved = await createSkill(form, channelId);
      }
      onSaved(saved);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save skill.');
    } finally {
      setSaving(false);
    }
  };

  // ── Validate ──────────────────────────────────────────────────────────────

  const handleValidate = async () => {
    setValidateStatus('loading');
    setValidateMsg('');
    try {
      let result: ValidateResult;
      if (form.protocol === 'MCP') {
        // auth_config can carry stale stdio_command/stdio_args from a prior edit where this
        // skill was configured as stdio transport — authConfigFromApi copies them in
        // unconditionally on load. Strip them here for non-stdio transports so Validate
        // doesn't send them, mirroring the transport guard createSkill/updateSkill already
        // apply at save time (skillsService.ts buildAuthConfigForSave-equivalent block).
        const authConfigForValidate = stripExtraHeaderIds({ ...(form.auth_config ?? { type: 'none' }) });
        if (form.transport !== 'stdio') {
          delete authConfigForValidate.stdio_command;
          delete authConfigForValidate.stdio_args;
        }
        result = await validateMcpSkill(
          form.mcp_server_url ?? '',
          authConfigForValidate
        );
      } else {
        // Additional Headers renders for every auth type regardless of protocol, so a
        // REST skill's auth_config can carry extra_headers too — same id-strip as the
        // MCP branch above applies here.
        result = await validateRestSkill(
          form.base_url ?? '',
          stripExtraHeaderIds(form.auth_config ?? { type: 'none' })
        );
      }

      if (result.warning) {
        setValidateStatus('warning');
        setValidateMsg(result.warning);
      } else {
        setValidateStatus(result.is_valid ? 'success' : 'error');
        setValidateMsg(result.message);
      }

      // Store discovered tool_list on the form for Save
      if (result.tool_list?.length) {
        update({ tool_list: result.tool_list });
      }
    } catch (err) {
      setValidateStatus('error');
      setValidateMsg(err instanceof Error ? err.message : 'Validation failed.');
    }
  };

  // ── stdio explicit validate ───────────────────────────────────────────────

  const handleStdioValidate = async () => {
    setValidateStatus('loading');
    setValidateMsg('');
    setStdioValidatePassed(false);
    const authConfigForValidate: Record<string, unknown> = {
      transport: 'stdio',
      stdio_command: form.stdio_command,
      stdio_args: buildStdioArgs(form.stdio_package, form.stdio_version),
    };
    for (const row of form.stdio_env_rows ?? []) {
      const key = row.key.trim();
      if (key) authConfigForValidate[`env_${key}`] = row.value;
    }
    try {
      const result = await validateSkillConnection(authConfigForValidate);
      if (result.status === 'error') {
        setValidateStatus('error');
        setValidateMsg(result.message || 'Connection failed.');
      } else {
        setValidateStatus('success');
        setValidateMsg(
          `Connection OK${result.tools?.length ? ` — ${result.tools.length} tools: ${result.tools.join(', ')}` : ''}`
        );
        setStdioValidatePassed(true);
      }
    } catch (err) {
      setValidateStatus('error');
      setValidateMsg(err instanceof Error ? err.message : 'Validation failed.');
    }
  };

  // ── Router emails chip helpers ────────────────────────────────────────────

  const routerEmailChips = useMemo(
    () => (form.router_emails ?? '').split(',').map((e) => e.trim()).filter(Boolean),
    [form.router_emails]
  );

  const addRouterEmailChip = (email: string) => {
    const trimmed = email.trim();
    if (!trimmed || routerEmailChips.includes(trimmed)) return;
    update({ router_emails: [...routerEmailChips, trimmed].join(', ') });
    setRouterEmailInput('');
  };

  const removeRouterEmailChip = (email: string) => {
    update({ router_emails: routerEmailChips.filter((e) => e !== email).join(', ') });
  };

  // ── Discover Schema ───────────────────────────────────────────────────────

  const handleDiscoverSchema = async () => {
    if (!form.base_url || !form.endpoint_path) return;
    setDiscoverLoading(true);
    setDiscoverError('');
    try {
      const { payload_schema, response_schema } = await discoverSchema(
        form.base_url,
        form.endpoint_path,
        form.method ?? 'POST',
        form.auth_config ?? { type: 'none' }
      );
      update({ payload_schema, response_schema });
      setDiscoverError('');
    } catch (err) {
      setDiscoverError(err instanceof Error ? err.message : 'Schema discovery failed.');
    } finally {
      setDiscoverLoading(false);
    }
  };

  // ── Knowledge Files (skill-specific inline upload) ───────────────────────────

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setPendingFiles(Array.from(e.target.files));
  };

  const handleUploadEmbed = async () => {
    if (pendingFiles.length === 0) return;
    setUploading(true);
    setUploadError('');
    try {
      const activeTag: 'knowledge_base' | 'response_template' = datasourceTab === 0 ? 'knowledge_base' : 'response_template';
      const newFiles = await uploadKnowledgeFiles(pendingFiles, channelId, activeTag);
      if (!newFiles.length) {
        setUploadError('Upload completed but no files were registered. Please try again.');
        return;
      }
      setForm((prev) => ({
        ...prev,
        knowledge_files: [...prev.knowledge_files, ...newFiles],
      }));
      setPendingFiles([]);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  // Called by AddLogDataSourceModal after step-1 upload succeeds; polls until step-2 DB records appear.
  const handleDatasourceAdded = async (pendingFilenames?: string[]) => {
    if (!pendingFilenames?.length) return;
    setAddDsModalOpen(false);
    setLinkingFiles(true);
    setLinkError('');
    // Capture generation at call time; any open/close transition increments the ref, making this stale.
    const generation = ++datasourceLinkGenerationRef.current;
    const activeTag: 'knowledge_base' | 'response_template' =
      datasourceTab === 0 ? 'knowledge_base' : 'response_template';

    let found: KnowledgeFile[] = [];
    for (let attempt = 0; attempt < 15; attempt++) {
      if (generation !== datasourceLinkGenerationRef.current) return; // dialog closed/switched — bail
      try {
        // M-1: Matches by filename since step-1 upload returns no IDs.
        // Assumes filenames are unique enough within this channel's polling window (accepted tradeoff).
        found = await fetchKnowledgeFilesByFilenames(channelId, pendingFilenames);
        if (found.length > 0) break;
      } catch {
        // ignore and retry
      }
      if (generation !== datasourceLinkGenerationRef.current) return; // cancelled during sleep
      await new Promise<void>((res) => setTimeout(res, 1500));
    }

    if (generation !== datasourceLinkGenerationRef.current) return; // cancelled after final poll

    if (found.length > 0) {
      const tagged = found.map((kf) => ({ ...kf, tag: activeTag }));
      setForm((prev) => ({
        ...prev,
        knowledge_files: [
          ...prev.knowledge_files.filter(
            (kf) => !tagged.some((t) => t.datasource_id === kf.datasource_id)
          ),
          ...tagged,
        ],
      }));
      // Register IDs in the module-level registry so DataSourcesPanel excludes them immediately,
      // even before the skill is saved (backend list may not yet include these knowledge_files).
      claimSkillDatasourceIds(tagged.map((kf) => kf.datasource_id));
      // M-2: Also force-refresh skills cache for the next DataSourcesPanel open.
      fetchSkills(channelId, { force: true }).catch(() => {});
    } else {
      setLinkError(
        'Files were added but could not be linked to this skill yet. Save the skill and refresh to check.'
      );
    }
    setLinkingFiles(false);
  };

  const handleRemoveFile = async (datasourceId: string, index: number) => {
    if (!datasourceId) {
      setForm((prev) => ({
        ...prev,
        knowledge_files: prev.knowledge_files.filter((_, i) => i !== index),
      }));
      return;
    }

    setRemovingFileId(datasourceId);
    try {
      const remaining = form.knowledge_files.filter((f) => f.datasource_id !== datasourceId);
      if (form.id) {
        await apiRemoveKnowledgeFile(form.id, datasourceId, remaining);
      } else {
        await deleteDatasource(datasourceId);
      }
      setForm((prev) => ({ ...prev, knowledge_files: remaining }));
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Failed to remove file.');
    } finally {
      setRemovingFileId(null);
    }
  };

  const isExternal = form.type === 'external';
  const isRest = isExternal && form.protocol === 'REST';
  const isMcp = isExternal && form.protocol === 'MCP';
  const isStdio = isMcp && form.transport === 'stdio';

  const kbFiles = form.knowledge_files.filter((kf) => !kf.tag || kf.tag === 'knowledge_base');
  const rtFiles = form.knowledge_files.filter((kf) => kf.tag === 'response_template');
  const activeFiles = datasourceTab === 0 ? kbFiles : rtFiles;

  const validateAlertSeverity = validateStatus === 'warning' ? 'warning'
    : validateStatus === 'success' ? 'success'
    : validateStatus === 'error' ? 'error'
    : 'info';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          borderBottom: `1px solid ${muiTheme.palette.divider}`,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {isEdit ? 'Edit Skill' : 'Create Skill'}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* ── How it works strip ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            px: 2,
            py: 1.25,
            borderRadius: 1.5,
            backgroundColor: muiTheme.palette.mode === 'dark'
              ? 'rgba(99,102,241,0.12)'
              : 'rgba(99,102,241,0.07)',
            border: `1px solid ${muiTheme.palette.mode === 'dark' ? 'rgba(99,102,241,0.3)' : 'rgba(99,102,241,0.2)'}`,
          }}
        >
          {/* <EmailOutlinedIcon
            fontSize="small"
            sx={{ color: 'text.secondary', mt: 0.25, flexShrink: 0 }}
          /> */}
          <Typography variant="body2" color="text.secondary" component="p" sx={{ m: 0, lineHeight: 1.5 }}>
          📧Email arrives → Skill reads it &amp; applies your rules → Calls your API / Takes action
          </Typography>
        </Box>

        {/* ── 2a: Skill Type ── */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary" gutterBottom>
            Skill Type
          </Typography>
          <Typography variant="body2" color="text.primary">
            {form.type === 'internal' ? 'Internal' : 'External'}
          </Typography>
        </Box>

        {/* ── Name ── */}
        <TextField
          label="Name"
          size="small"
          fullWidth
          required
          sx={skillDialogTextFieldSx}
          {...requiredTextFieldLabelProps}
          value={form.name}
          onChange={(e) => update({ name: e.target.value })}
          placeholder="e.g. Invoice Approver, Lead Qualifier, Ticket Classifier"
          helperText={
            form.type === 'internal'
              ? 'Must match a registered chain name (e.g. PII Secured)'
              : 'A short unique name. Used in logs and agent routing.'
          }
        />

        {/* ── Description ── */}
        <TextField
          label="Description"
          size="small"
          fullWidth
          multiline
          rows={2}
          sx={skillDialogTextFieldSx}
          value={form.description}
          onChange={(e) => update({ description: e.target.value })}
          placeholder="e.g. Reads incoming emails, applies scoring rules, and forwards qualified items to the right team"
          helperText="what does this skill decide or do? The routing agent reads this to know when to hand off to this skill."
        />

        {/* ── Business Rules ── */}
        <Box sx={{ position: 'relative' }}>
          <TextField
            label="Business Rules"
            size="small"
            fullWidth
            multiline
            rows={6}
            sx={skillDialogTextFieldSx}
            value={form.business_rules}
            onChange={(e) => update({ business_rules: e.target.value })}
            placeholder={`What should this skill look for in the email?\ne.g. Check if the email contains a request, complaint, or approval.\n\nWhat should it decide or score?\ne.g. Classify as High / Medium / Low priority based on urgency and content.\n\nWhat should it do with the result?\ne.g. If High priority — call the API and send a summary to the router emails below.\n     If Low priority — do nothing.`}
            helperText={
              businessRulesLoading
                ? 'Generating business rules from Name and Description...'
                : "Write in plain English. You don't need to write code or prompts — describe the rules like you'd explain them to a new team member."
            }
            InputProps={{
              readOnly: businessRulesLoading,
            }}
          />
          {businessRulesLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: '1.375rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
                zIndex: 2,
              }}
            >
              <CircularProgress size={28} />
            </Box>
          )}
        </Box>
        {businessRulesError && (
          <Alert severity="warning" sx={{ py: 0 }} onClose={() => setBusinessRulesError('')}>
            {businessRulesError}
          </Alert>
        )}

        {/* ── 2b: Protocol ── */}
        {isExternal && (
          <>
            <Divider />
            <Box>
              <Typography variant="subtitle2" fontWeight={600} color="text.secondary" gutterBottom>
                Protocol
              </Typography>
              <FormControl>
                <RadioGroup
                  row
                  value={form.protocol ?? 'REST'}
                  onChange={(e) => update({ protocol: e.target.value as Protocol })}
                >
                  <Tooltip title="Call any HTTP endpoint — your own backend, a SaaS API, or a webhook" placement="bottom">
                    <FormControlLabel value="REST" control={<Radio size="small" />} label="REST API" />
                  </Tooltip>
                  <Tooltip title="Connect via Model Context Protocol for tool-based integrations" placement="bottom">
                    <FormControlLabel value="MCP" control={<Radio size="small" />} label="MCP" />
                  </Tooltip>
                </RadioGroup>
              </FormControl>
            </Box>
          </>
        )}

        {/* ── 2c: REST fields ── */}
        {isRest && (
          <>
            {/* Base URL + Endpoint Path */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Base URL"
                size="small"
                fullWidth
                required
                sx={skillDialogTextFieldSx}
                {...requiredTextFieldLabelProps}
                value={form.base_url ?? ''}
                onChange={(e) => update({ base_url: e.target.value })}
                placeholder="https://your-api.com"
                helperText="Root URL of your API — do not include the path here."
                InputProps={{
                  endAdornment: (
                    <Tooltip title="Root URL of your API — e.g. https://your-api.com. Do not include the endpoint path here. The skill will POST the email content to Base URL + Endpoint Path.">
                      <IconButton size="small" aria-label="More info" sx={{ p: 0.25, color: 'text.secondary' }}>
                        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
              <TextField
                label="Endpoint Path"
                size="small"
                fullWidth
                required
                sx={skillDialogTextFieldSx}
                {...requiredTextFieldLabelProps}
                value={form.endpoint_path ?? ''}
                onChange={(e) => update({ endpoint_path: e.target.value })}
                placeholder="/process"
                helperText="Path appended to Base URL — must start with /."
                InputProps={{
                  endAdornment: (
                    <Tooltip title="Path appended to the Base URL — must start with /. Example: /process or /api/v1/handle. Your API receives the email data here, processes it, and returns a result.">
                      <IconButton size="small" aria-label="More info" sx={{ p: 0.25, color: 'text.secondary' }}>
                        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
            </Box>

            {/* Method */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControl size="small" sx={{ maxWidth: 200 }}>
                <InputLabel>Method</InputLabel>
                <Select
                  value={form.method ?? 'POST'}
                  label="Method"
                  onChange={(e) => update({ method: e.target.value as HttpMethod })}
                >
                  {(['POST', 'GET', 'PUT', 'PATCH', 'DELETE'] as HttpMethod[]).map((m) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title="HTTP method used to send the email data as a structured body to the API endpoint. POST is recommended for most integrations.">
                <IconButton size="small" sx={{ p: 0.25, color: 'text.secondary' }}>
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Auth */}
            <Box ref={authSectionRef}>
              <SkillAuthConfigForm
                value={(form.auth_config ?? { type: 'none' }) as SkillAuthConfig}
                onChange={(ac) => update({ auth_config: ac })}
              />
            </Box>

            {/* Payload Schema */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight={500}>Payload Schema (JSON)</Typography>
                  <Tooltip title="Tell the skill what data to send to your API. Use {{field}} placeholders — the agent replaces them with real values from the email at runtime. Example fields: {{email_body}}, {{sender_email}}, {{subject}}.">
                    <IconButton size="small" sx={{ p: 0.25, color: 'text.secondary' }}>
                      <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ textTransform: 'none' }}
                  disabled={discoverLoading || !form.base_url || !form.endpoint_path}
                  onClick={handleDiscoverSchema}
                  startIcon={discoverLoading ? <CircularProgress size={12} color="inherit" /> : undefined}
                >
                  {discoverLoading ? 'Discovering…' : 'Discover Schema'}
                </Button>
              </Box>
              {discoverError && (
                <Alert severity="warning" sx={{ mb: 1, py: 0 }} onClose={() => setDiscoverError('')}>
                  {discoverError}
                </Alert>
              )}
              <TextField
                size="small"
                fullWidth
                multiline
                rows={5}
                sx={skillDialogJsonTextFieldSx}
                value={form.payload_schema ?? ''}
                onChange={(e) => update({ payload_schema: e.target.value })}
                placeholder={'{\n  "email_body": "{{email_body}}",\n  "sender": "{{sender_email}}",\n  "subject": "{{subject}}"\n}'}
              />
            </Box>

            {/* Response Schema */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="body2" fontWeight={500}>Response Schema (JSON)</Typography>
                <Tooltip title="Describe the JSON structure your API returns. The skill uses this to parse and act on the response at runtime.">
                  <IconButton size="small" sx={{ p: 0.25, color: 'text.secondary' }}>
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <TextField
                size="small"
                fullWidth
                multiline
                rows={4}
                sx={skillDialogJsonTextFieldSx}
                value={form.response_schema ?? ''}
                onChange={(e) => update({ response_schema: e.target.value })}
                placeholder={'{\n  "email_body": "{{email_body}}",\n  "sender": "{{sender_email}}",\n  "subject": "{{subject}}"\n}'}
              />
            </Box>
          </>
        )}

        {/* ── 2d: MCP fields ── */}
        {isMcp && (
          <>
            <FormControl size="small" sx={{ maxWidth: 260 }}>
              <InputLabel>Transport</InputLabel>
              <Select
                value={form.transport ?? 'SSE'}
                label="Transport"
                onChange={(e) => update({ transport: e.target.value as Transport })}
              >
                <MenuItem value="SSE">SSE (legacy)</MenuItem>
                <MenuItem value="Streamable HTTP">Streamable HTTP (2025+)</MenuItem>
                <MenuItem value="stdio">stdio (subprocess)</MenuItem>
              </Select>
            </FormControl>

            {!isStdio && (
              <TextField
                label="MCP Server URL"
                size="small"
                fullWidth
                required
                sx={skillDialogTextFieldSx}
                {...requiredTextFieldLabelProps}
                value={form.mcp_server_url ?? ''}
                onChange={(e) => update({ mcp_server_url: e.target.value })}
                placeholder="http://mcp-server:8080/sse"
                helperText="Full URL of your running MCP server — find it in your server's deployment config."
                InputProps={{
                  endAdornment: (
                    <Tooltip title="The full URL of your running MCP server. For SSE, this is the /sse endpoint; for Streamable HTTP, it is the base HTTP endpoint. You can find this in your MCP server's documentation or deployment config.">
                      <IconButton size="small" aria-label="More info" sx={{ p: 0.25, color: 'text.secondary' }}>
                        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
            )}

            {isStdio ? (
              <StdioMcpFields
                value={{
                  stdio_catalog: form.stdio_catalog ?? 'custom',
                  stdio_command: form.stdio_command ?? 'uvx',
                  stdio_package: form.stdio_package ?? '',
                  stdio_version: form.stdio_version ?? '',
                  stdio_env_rows: form.stdio_env_rows ?? [],
                }}
                onChange={(partial) => update(partial)}
                onPrefillBusinessRules={(text) => update({ business_rules: text })}
              />
            ) : (
              <Box ref={authSectionRef}>
                <SkillAuthConfigForm
                  value={(form.auth_config ?? { type: 'none' }) as SkillAuthConfig}
                  onChange={(ac) => update({ auth_config: ac })}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, px: 0.5 }}>
                  If your API requires a key or token to accept requests, configure it here. Credentials are encrypted and never visible after saving.
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* ── Router Emails (chip input) ── */}
        {isExternal && (
          <Box>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 0.75 }}>
              Router Emails
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                — who receives escalations / summaries
              </Typography>
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.75,
                p: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                minHeight: 42,
                alignItems: 'center',
                cursor: 'text',
                '&:focus-within': { borderColor: 'primary.main', boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}22` },
              }}
              onClick={() => document.getElementById('routerEmailInput')?.focus()}
            >
              {routerEmailChips.map((email) => (
                <Chip
                  key={email}
                  label={email}
                  size="small"
                  onDelete={() => removeRouterEmailChip(email)}
                  sx={{ fontSize: '0.75rem' }}
                />
              ))}
              <Box
                component="input"
                id="routerEmailInput"
                value={routerEmailInput}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRouterEmailInput(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addRouterEmailChip(routerEmailInput.replace(/,$/, ''));
                  } else if (e.key === 'Backspace' && routerEmailInput === '' && routerEmailChips.length > 0) {
                    removeRouterEmailChip(routerEmailChips[routerEmailChips.length - 1]);
                  }
                }}
                onBlur={() => { if (routerEmailInput.trim()) addRouterEmailChip(routerEmailInput); }}
                placeholder={routerEmailChips.length === 0 ? 'Add email and press Enter…' : ''}
                sx={{
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  fontSize: '0.8125rem',
                  color: 'text.primary',
                  minWidth: 180,
                  flex: 1,
                  fontFamily: 'inherit',
                  p: 0,
                }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              After the skill runs, a summary is sent here. Leave blank if no notification is needed.
            </Typography>
          </Box>
        )}

        {/* ── Validate result banner ── */}
        {validateStatus !== 'idle' && (
          <Alert
            severity={validateAlertSeverity}
            icon={
              validateStatus === 'loading'
                ? <CircularProgress size={16} />
                : validateStatus === 'success'
                ? <CheckCircleOutlineIcon fontSize="small" />
                : <ErrorOutlineIcon fontSize="small" />
            }
          >
            {validateStatus === 'loading' ? 'Validating connection…' : validateMsg}
          </Alert>
        )}

        {/* ── Save error banner ── */}
        {saveError && (
          <Alert severity="error" onClose={() => setSaveError('')}>
            {saveError}
          </Alert>
        )}

        {/* ── 2e: Datasources (Knowledge Base / Response Templates) ── */}
        <Divider />
        <Box sx={{ border: `1px solid ${muiTheme.palette.divider}`, borderRadius: 1, width: '100%' }}>
          {/* Section header */}
          <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Datasources
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Attach documents the agent can consult when processing emails
            </Typography>
          </Box>

          {/* Tab bar */}
          <Tabs
            value={datasourceTab}
            onChange={(_: React.SyntheticEvent, v: number) => {
              setDatasourceTab(v as 0 | 1);
              setPendingFiles([]);
              setUploadError('');
            }}
            sx={{ borderBottom: `1px solid ${muiTheme.palette.divider}` }}
          >
            <Tab
              value={0}
              label={kbFiles.length > 0 ? `Knowledge Base (${kbFiles.length})` : 'Knowledge Base'}
              sx={{ textTransform: 'none', fontSize: '0.8125rem' }}
            />
            <Tab
              value={1}
              label={rtFiles.length > 0 ? `Response Templates (${rtFiles.length})` : 'Response Templates'}
              sx={{ textTransform: 'none', fontSize: '0.8125rem' }}
            />
          </Tabs>

          {/* Tab content */}
          <Box sx={{ p: 2 }}>
            {activeFiles.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {datasourceTab === 0
                  ? 'No knowledge files attached. Upload reference documents — policies, scoring guides, FAQs — that the agent will search when applying your business rules.'
                  : 'No response templates attached. Upload template documents — email drafts, reply formats, standard responses — that the agent uses to craft replies.'}
                {' '}Supported formats: csv, xlsx, xls, json, pdf, docx, doc, txt, pptx, ppt, md (up to 100 MB per file).
              </Typography>
            ) : (
              <Stack spacing={1} sx={{ mb: 2 }}>
                {activeFiles.map((kf) => {
                  const globalIndex = form.knowledge_files.indexOf(kf);
                  return (
                    <Box
                      key={kf.datasource_id || `knowledge-file-${globalIndex}-${kf.filename}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 1.5,
                        py: 0.75,
                        border: `1px solid ${muiTheme.palette.divider}`,
                        borderRadius: 1,
                        backgroundColor: muiTheme.palette.action.hover,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AttachFileIcon fontSize="small" color="action" />
                        <Typography variant="body2">{kf.filename}</Typography>
                        <Chip
                          label={datasourceTab === 0 ? 'KB' : 'RT'}
                          size="small"
                          variant="outlined"
                          sx={{ height: 16, fontSize: '0.6rem', borderRadius: 0.5 }}
                        />
                      </Box>
                      <Tooltip title="Delete file">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={!kf.datasource_id || removingFileId === kf.datasource_id}
                            onClick={() => handleRemoveFile(kf.datasource_id, globalIndex)}
                          >
                            {removingFileId === kf.datasource_id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <DeleteIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Stack>
            )}

            {uploadError && (
              <Alert severity="error" sx={{ mb: 1 }} onClose={() => setUploadError('')}>
                {uploadError}
              </Alert>
            )}

            {linkError && (
              <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setLinkError('')}>
                {linkError}
              </Alert>
            )}

            {linkingFiles && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Linking files to skill…
                </Typography>
              </Box>
            )}

            {/* "Add Data Source" button — opens the same rich modal as the channel datasource flow */}
            <Button
              variant="outlined"
              fullWidth
              startIcon={<AttachFileIcon />}
              disabled={linkingFiles}
              onClick={() => setAddDsModalOpen(true)}
              sx={{
                textTransform: 'none',
                borderStyle: 'dashed',
                py: 1.5,
                borderColor: muiTheme.palette.divider,
                color: muiTheme.palette.text.secondary,
                '&:hover': {
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  backgroundColor: 'transparent',
                },
              }}
            >
              Add Data Source
            </Button>

            {/* Full datasource modal — skill-specific; files are filtered out of the channel panel */}
            <AddLogDataSourceModal
              open={addDsModalOpen}
              onClose={() => setAddDsModalOpen(false)}
              onDataSourceAdded={handleDatasourceAdded}
              channelId={channelId}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${muiTheme.palette.divider}`, gap: 1 }}>
        <Button variant="outlined" onClick={onClose} sx={{ textTransform: 'none' }} disabled={saving}>
          Cancel
        </Button>

        {/* Validate button — REST/SSE/Streamable HTTP */}
        {isExternal && !isStdio && (
          <Tooltip title="Test your API connection and check that the payload schema is valid before saving">
            <span>
              <Button
                variant="outlined"
                color="secondary"
                disabled={validateStatus === 'loading' || saving}
                onClick={handleValidate}
                sx={{ textTransform: 'none' }}
              >
                Validate
              </Button>
            </span>
          </Tooltip>
        )}

        {/* Explicit validate button — stdio only */}
        {isStdio && (
          <Tooltip title="Spawn the subprocess and confirm the MCP server starts and returns tools. Required before saving.">
            <span>
              <Button
                variant="outlined"
                color={stdioValidatePassed ? 'success' : 'secondary'}
                disabled={validateStatus === 'loading' || saving}
                onClick={handleStdioValidate}
                startIcon={validateStatus === 'loading' ? <CircularProgress size={14} color="inherit" /> : undefined}
                sx={{ textTransform: 'none' }}
              >
                {validateStatus === 'loading' ? 'Validating…' : stdioValidatePassed ? 'Validated' : 'Validate Connection'}
              </Button>
            </span>
          </Tooltip>
        )}

        <Tooltip title={
          isStdio && !stdioValidatePassed
            ? 'Validate the connection first — the Save button unlocks once validation passes.'
            : 'Save and activate this skill. It will be available to attach to the channel.'
        }>
          <span>
            <Button
              variant="contained"
              disabled={!form.name.trim() || saving || (isStdio && !stdioValidatePassed)}
              onClick={handleSave}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
              sx={{ textTransform: 'none' }}
            >
              {saving ? 'Saving…' : 'Save Skill'}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

// ─── Delete Confirm Dialog ────────────────────────────────────────────────────

interface DeleteDialogProps {
  open: boolean;
  skillName: string;
  deleting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const DeleteDialog: React.FC<DeleteDialogProps> = ({ open, skillName, deleting, onClose, onConfirm }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle>Delete Skill</DialogTitle>
    <DialogContent>
      <Typography>
        Are you sure you want to delete <strong>{skillName}</strong>? This action cannot be undone.
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onClose} variant="outlined" sx={{ textTransform: 'none' }} disabled={deleting}>
        Cancel
      </Button>
      <Button
        onClick={onConfirm}
        variant="contained"
        color="error"
        disabled={deleting}
        startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : undefined}
        sx={{ textTransform: 'none' }}
      >
        {deleting ? 'Deleting…' : 'Delete'}
      </Button>
    </DialogActions>
  </Dialog>
);

// ─── SkillsTab (main) ─────────────────────────────────────────────────────────

interface SkillsTabProps {
  channelId?: string;
  workspaceTag?: string;
  isChannelArchived?: boolean;
  connectSkillsWizardMode?: boolean;
  /** Opens the create-skill dialog once when reached through an add-skill deep link. */
  initialOpenAddSkill?: boolean;
  /** Called after the deep-link dialog request has been consumed. */
  onInitialAddSkillOpened?: () => void;
}

const SkillsTab: React.FC<SkillsTabProps> = ({
  channelId = '',
  workspaceTag,
  isChannelArchived = false,
  connectSkillsWizardMode = false,
  initialOpenAddSkill = false,
  onInitialAddSkillOpened,
}) => {
  const muiTheme = useMuiTheme();
  const { isDarkMode } = useTheme();

  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editSkill, setEditSkill] = useState<Skill | null>(null);
  const initialAddSkillOpenedRef = React.useRef(false);

  const [deleteTarget, setDeleteTarget] = useState<Skill | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const [togglingId, setTogglingId] = useState<string | null>(null);

  // ── Load skills on mount / channelId change (one GET per channel) ───────────
  // Do not abort on effect cleanup — canceled requests hide "Request Method: GET" in DevTools.

  const loadGenerationRef = React.useRef(0);

  const loadSkills = React.useCallback(async (force = false) => {
    if (!channelId) return;

    const generation = ++loadGenerationRef.current;

    setLoading(true);
    setLoadError('');
    try {
      const data = await fetchSkills(channelId, { force });
      if (generation !== loadGenerationRef.current) return;
      setSkills(data);
    } catch (err) {
      if (generation !== loadGenerationRef.current) return;
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setLoadError(err instanceof Error ? err.message : 'Failed to load skills.');
    } finally {
      if (generation === loadGenerationRef.current) {
        setLoading(false);
      }
    }
  }, [channelId]);

  useEffect(() => {
    if (!channelId) return;
    loadSkills(false);
    return () => {
      loadGenerationRef.current += 1;
    };
  }, [channelId, loadSkills]);

  // The settings deep link selects this tab, but the add-skill action also needs to
  // open the create dialog. Consume it once so normal tab navigation stays predictable.
  useEffect(() => {
    if (!initialOpenAddSkill) {
      initialAddSkillOpenedRef.current = false;
      return;
    }
    if (!channelId || isChannelArchived || initialAddSkillOpenedRef.current) return;

    initialAddSkillOpenedRef.current = true;
    setEditSkill(null);
    setFocusAuthOnOpen(false);
    setDialogOpen(true);
    onInitialAddSkillOpened?.();
  }, [initialOpenAddSkill, channelId, isChannelArchived, onInitialAddSkillOpened]);

  // ── Dialog open/close ─────────────────────────────────────────────────────

  const handleAddClick = () => {
    setEditSkill(null);
    setDialogOpen(true);
  };

  const [focusAuthOnOpen, setFocusAuthOnOpen] = useState(false);

  const handleEditClick = (s: Skill) => {
    setFocusAuthOnOpen(false);
    setEditSkill(s);
    setDialogOpen(true);
  };

  const handleAuthBadgeClick = (s: Skill) => {
    setFocusAuthOnOpen(true);
    setEditSkill(s);
    setDialogOpen(true);
  };

  // ── Save callback (after create/update inside dialog) ─────────────────────

  const handleSaved = (saved: Skill) => {
    setSkills((prev) => {
      const idx = prev.findIndex((s) => s.id === saved.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = saved;
        return next;
      }
      return [...prev, saved];
    });
    setDialogOpen(false);
  };

  // ── Toggle active ─────────────────────────────────────────────────────────

  const handleToggleActive = async (id: string, value: boolean) => {
    setTogglingId(id);
    // Optimistic update
    setSkills((prev) => prev.map((s) => (s.id === id ? { ...s, is_active: value } : s)));
    try {
      await toggleSkillActive(id, value);
    } catch (_) {
      // Revert on failure
      setSkills((prev) => prev.map((s) => (s.id === id ? { ...s, is_active: !value } : s)));
    } finally {
      setTogglingId(null);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDeleteClick = (s: Skill) => {
    if (s.is_preloaded) return;
    setDeleteTarget(s);
    setDeleteError('');
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await apiDeleteSkill(deleteTarget);
      setSkills((prev) => prev.filter((s) => s.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete skill.');
    } finally {
      setDeleting(false);
    }
  };

  // ── Table filtering ───────────────────────────────────────────────────────

  const tableSkills = useMemo(() => skills, [skills]);

  const protocolLabel = (skill: Skill) => {
    if (skill.type === 'internal') return '—';
    return skill.protocol ?? '—';
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Skills
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage AI skills connected to this channel
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh skills list">
            <span>
              <IconButton size="small" onClick={() => loadSkills(true)} disabled={loading}>
                {loading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
            disabled={isChannelArchived || !channelId}
            onClick={handleAddClick}
            sx={{ textTransform: 'none' }}
          >
            Add Skill
          </Button>
        </Box>
      </Box>

      {/* Load error */}
      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }} action={
          <Button size="small" color="inherit" onClick={() => loadSkills(true)}>Retry</Button>
        }>
          {loadError}
        </Alert>
      )}

      {/* Delete error */}
      {deleteError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDeleteError('')}>
          {deleteError}
        </Alert>
      )}

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          ...getTableContainerStyles(),
          backgroundColor: 'background.paper',
          boxShadow: 2,
          overflow: 'auto',
        }}
      >
        <Table stickyHeader aria-label="Skills table">
          <TableHead>
            <TableRow>
              {['Name', 'Type', 'Protocol', 'Auth', 'Active', 'Preloaded', 'Actions'].map((col) => (
                <TableCell
                  key={col}
                  sx={{ ...getTableHeaderStyles(isDarkMode), ...getTableCellStyles() }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ ...getTableCellStyles(), py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            )}
            {!loading && tableSkills.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ ...getTableCellStyles(), py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    {connectSkillsWizardMode
                      ? 'No skills to display for this channel.'
                      : <>No skills configured. Click <strong>Add Skill</strong> to get started.</>}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading && tableSkills.map((skill) => (
              <TableRow key={skill.id} hover sx={getTableRowStyles()}>
                {/* Name */}
                <TableCell sx={getTableCellStyles()}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {skill.name}
                    </Typography>
                    {skill.description && (
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 260, display: 'block' }}>
                        {skill.description}
                      </Typography>
                    )}
                  </Box>
                </TableCell>

                {/* Type */}
                <TableCell sx={getTableCellStyles()}>
                  <Chip
                    label={skill.type}
                    size="small"
                    variant="outlined"
                    color={skill.type === 'internal' ? 'primary' : 'secondary'}
                    sx={{ textTransform: 'capitalize', fontSize: '0.75rem' }}
                  />
                </TableCell>

                {/* Protocol */}
                <TableCell sx={getTableCellStyles()}>
                  <Typography variant="body2" color="text.secondary">
                    {protocolLabel(skill)}
                  </Typography>
                </TableCell>

                {/* Auth status */}
                <TableCell sx={getTableCellStyles()}>
                  <SkillAuthStatusBadge
                    authConfig={(skill.auth_config ?? null) as SkillAuthConfig | null}
                    onClick={() => handleAuthBadgeClick(skill)}
                  />
                </TableCell>

                {/* Active toggle — behaves identically in and out of the Connect Skills wizard
                    step now (skills_enable_in_channel_configure): previously this was forced
                    unchecked and disabled for internal-type skills whenever
                    connectSkillsWizardMode was true, and handleToggleActive silently no-op'd
                    for every skill type in wizard mode (channelId is already a real, saved
                    channel by this step, so there's no reason the toggle shouldn't persist
                    immediately like it does everywhere else). */}
                <TableCell sx={getTableCellStyles()}>
                  <Switch
                    checked={skill.is_active}
                    size="small"
                    disabled={isChannelArchived || togglingId === skill.id}
                    onChange={(e) => handleToggleActive(skill.id, e.target.checked)}
                    color="primary"
                  />
                </TableCell>

                {/* Preloaded */}
                <TableCell sx={getTableCellStyles()}>
                  {skill.is_preloaded ? (
                    <Chip label="Preloaded" size="small" color="default" sx={{ fontSize: '0.75rem' }} />
                  ) : (
                    <Typography variant="body2" color="text.disabled">—</Typography>
                  )}
                </TableCell>

                {/* Actions */}
                <TableCell sx={getTableCellStyles()}>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Edit skill">
                      <IconButton
                        size="small"
                        disabled={isChannelArchived}
                        onClick={() => handleEditClick(skill)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={skill.is_preloaded ? 'Preloaded skills cannot be deleted' : 'Delete skill'}>
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={isChannelArchived || skill.is_preloaded}
                          onClick={() => handleDeleteClick(skill)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create / Edit Dialog */}
      <SkillDialog
        open={dialogOpen}
        skill={editSkill}
        channelId={channelId}
        workspaceTag={workspaceTag}
        onClose={() => { setDialogOpen(false); setFocusAuthOnOpen(false); }}
        onSaved={handleSaved}
        disableInternalSkillType={connectSkillsWizardMode}
        focusAuth={focusAuthOnOpen}
      />

      {/* Delete Confirm */}
      <DeleteDialog
        open={Boolean(deleteTarget)}
        skillName={deleteTarget?.name ?? ''}
        deleting={deleting}
        onClose={() => { if (!deleting) setDeleteTarget(null); }}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};

export default SkillsTab;
